import {
  cloneElement,
  isValidElement,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from "react";
import MuiTooltip, { type TooltipProps as MuiTooltipProps } from "@mui/material/Tooltip";
import ClickAwayListener from "@mui/material/ClickAwayListener";

// Thin wrapper mapping antd <Tooltip> API onto MUI Tooltip, so the ~25 call
// sites (which use antd-specific props: overlay*, mouseEnter/LeaveDelay,
// destroyTooltipOnHide, trigger="click"/array, arrow-object, onOpenChange)
// keep feature parity via an import-only swap. Same justification as the Tag
// wrapper: recurring antd-specific API across many sites.

export type TooltipPlacement = MuiTooltipProps["placement"];

type TriggerMode = "hover" | "click" | "focus";

export interface TooltipProps {
  title: ReactNode;
  children: ReactNode;
  placement?: TooltipPlacement;
  /** antd trigger; a single mode or an array. Only click vs hover matters here. */
  trigger?: TriggerMode | TriggerMode[];
  /** antd delays are in SECONDS; MUI wants ms. */
  mouseEnterDelay?: number;
  mouseLeaveDelay?: number;
  overlayClassName?: string;
  overlayStyle?: CSSProperties;
  overlayInnerStyle?: CSSProperties;
  /** antd controlled visibility. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  // Accepted for API compatibility, no MUI equivalent (ignored):
  destroyTooltipOnHide?: boolean;
  align?: unknown;
  arrow?: unknown; // antd arrow can be boolean or object; MUI arrow is boolean (we enable it)
  arrowPointAtCenter?: boolean;
  getPopupContainer?: unknown;
}

export const Tooltip = ({
  title,
  children,
  placement,
  trigger = "hover",
  mouseEnterDelay,
  mouseLeaveDelay,
  overlayClassName,
  overlayStyle,
  overlayInnerStyle,
  open,
  onOpenChange,
  arrow,
  // Parents (e.g. the shared Popover) inject handlers like onClick onto their
  // child via cloneElement. Capture them and forward to the trigger element below.
  ...rest
}: TooltipProps & Record<string, unknown>) => {
  const [clickOpen, setClickOpen] = useState(false);

  // Rich (JSX card) tooltip content — e.g. the fund preview — renders its own white
  // surface. MUI's default tooltip is a dark padded box, which showed as a "black
  // background" around the card. Strip the dark surface for non-string content so the
  // card styles itself; plain string tooltips keep MUI's normal dark look.
  const richContent = title != null && typeof title !== "string";
  const slotProps: MuiTooltipProps["slotProps"] = {
    tooltip: {
      ...(overlayClassName ? { className: overlayClassName } : {}),
      ...(overlayInnerStyle ? { style: overlayInnerStyle } : {}),
      ...(richContent
        ? { sx: { backgroundColor: "transparent", color: "inherit", p: 0, maxWidth: "none", boxShadow: "none" } }
        : {}),
    },
    ...(overlayStyle ? { popper: { style: overlayStyle } } : {}),
  };

  // MUI Tooltip requires a single ref-holding element child; wrap anything else.
  const rawChild: ReactElement = isValidElement(children) ? children : <span>{children}</span>;
  // Forward parent-injected props (e.g. Popover's onClick) onto the trigger,
  // merging onClick so both the child's own handler and the injected one fire.
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const childProps = rawChild.props as Record<string, any>;
  const injectedOnClick = rest.onClick as ((e: any) => void) | undefined;
  const child: ReactElement = Object.keys(rest).length
    ? cloneElement(rawChild, {
        ...rest,
        ...childProps,
        ...(injectedOnClick || childProps.onClick
          ? {
              onClick: (e: any) => {
                childProps.onClick?.(e);
                injectedOnClick?.(e);
              },
            }
          : {}),
      })
    : rawChild;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  const isClick = Array.isArray(trigger) ? trigger.includes("click") : trigger === "click";

  const shared = {
    title,
    placement,
    slotProps,
    arrow: Boolean(arrow),
    ...(mouseEnterDelay !== undefined ? { enterDelay: mouseEnterDelay * 1000 } : {}),
    ...(mouseLeaveDelay !== undefined ? { leaveDelay: mouseLeaveDelay * 1000 } : {}),
  } satisfies Partial<MuiTooltipProps>;

  if (isClick) {
    const toggle = () => {
      const next = !(open ?? clickOpen);
      setClickOpen(next);
      onOpenChange?.(next);
    };
    return (
      <ClickAwayListener onClickAway={() => setClickOpen(false)}>
        <MuiTooltip
          {...shared}
          open={open ?? clickOpen}
          disableHoverListener
          disableFocusListener
          disableTouchListener
        >
          <span onClick={toggle}>{child}</span>
        </MuiTooltip>
      </ClickAwayListener>
    );
  }

  return open !== undefined ? (
    <MuiTooltip {...shared} open={open}>
      {child}
    </MuiTooltip>
  ) : (
    <MuiTooltip {...shared}>{child}</MuiTooltip>
  );
};

export default Tooltip;
